import { useSyncExternalStore } from "react";

/**
 * spec08 FR22: Kbd hints are hidden on touch devices until a hardware keyboard is used.
 * Soft keyboards send text as key code 229 ("Unidentified"), so only real keys and
 * shortcuts count. Sets `data-keyboard` on <html>, which the CSS reads.
 */
export function isHardwareKey(event: KeyboardEvent): boolean {
  if (event.isComposing || event.keyCode === 229 || event.key === "Unidentified") return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  return event.key.length === 1 || /^(Escape|Tab|Arrow\w+)$/.test(event.key);
}

export function watchHardwareKeyboard() {
  const onKey = (event: KeyboardEvent) => {
    if (!isHardwareKey(event)) return;
    document.documentElement.dataset.keyboard = "";
    document.removeEventListener("keydown", onKey, true);
  };
  document.addEventListener("keydown", onKey, true);
}

/**
 * Whether the on-screen keyboard is open (spec08 FR12: the dock hides then). Touch devices
 * only: the visual viewport is much shorter than the window's full height.
 */
function softKeyboardOpen() {
  const viewport = globalThis.visualViewport;
  if (!viewport || !globalThis.matchMedia?.("(pointer: coarse)").matches) return false;
  return viewport.height < globalThis.screen.height * 0.6;
}

export function useSoftKeyboardOpen(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      globalThis.visualViewport?.addEventListener("resize", onChange);
      return () => globalThis.visualViewport?.removeEventListener("resize", onChange);
    },
    softKeyboardOpen,
    () => false,
  );
}
